// Shared playlists that carry your own tunes (docs/tune-library-plan.md §6,
// step E).
//
// A playlist row pointing at a tune you saved is a `saved` ref: it names a
// Score in YOUR device's storage, so on anyone else's device it could only
// ever report "unavailable". A shared playlist takes those tunes with it: each
// is written to your account marked `shared` (uploaded first if it was still
// on this device only) and the row points at it with an `account` ref — owner
// and tune id — which any signed-in member can open (migration 0010). On your
// own device an account ref opens from your saved tunes, instantly and
// offline. Sharing does this for the rows already there, and the sync does it
// for any saved tune anyone with edit access adds later: adding a tune to a
// shared playlist is sharing it.
//
// A shared tune opens live from the author's account, the way library tunes
// do: deleting it removes it for the playlist too, and a new Save a copy is a
// new tune (the playlist keeps pointing at the old one until someone swaps it).

import { getAuthState, getSupabaseRaw } from '@/auth/session';
import type { Score } from '@/music/score/types';
import { checkSavedTunes } from '@/storage/savedTunes';
import type { PlaylistDoc, PlaylistTuneRef } from '@/storage/playlists';
import { publishSetlist } from './sharedSetlists';

const TABLE = 'user_tunes';
/** Rows per upsert: a set is dozens of tunes, never hundreds. */
const CHUNK = 20;

type Row = Record<string, unknown>;
type Result = { data: Row[] | null; error: { message: string } | null };
type Filter = PromiseLike<Result> & { eq(column: string, value: string): Filter };

/** The slice of supabase-js this module uses (tests fake it). */
export interface SharedTunesClient {
  from(table: string): {
    select(columns: string): Filter;
    upsert(
      rows: Row[],
      options: { onConflict: string },
    ): PromiseLike<{ error: { message: string } | null }>;
  };
}

let testClient: SharedTunesClient | null = null;

function client(): SharedTunesClient | null {
  return testClient ?? (getSupabaseRaw() as SharedTunesClient | null);
}

/**
 * The copy of a set others will open: every `saved` row whose tune is on this
 * device points at the tune in the owner's account instead. A saved row whose
 * tune is not here (deleted since) is left as it is — there is nothing to
 * share. Pure.
 */
export function portableSetlist(
  list: PlaylistDoc,
  savedCharts: readonly Score[],
  owner: string,
): { doc: PlaylistDoc; tunes: Score[] } {
  const tunes: Score[] = [];
  const seen = new Set<string>();
  const entries = list.entries.map((entry) => {
    const saved = entry.ref;
    if (saved.kind !== 'saved') return entry;
    const tune = savedCharts.find((s) => s.id === saved.id);
    if (!tune?.id) return entry;
    if (!seen.has(tune.id)) {
      seen.add(tune.id);
      tunes.push(tune);
    }
    const ref: PlaylistTuneRef = {
      kind: 'account',
      id: tune.id,
      owner,
      title: saved.title,
      ...(saved.composer ? { composer: saved.composer } : {}),
    };
    return { ...entry, ref };
  });
  return { doc: { ...list, entries }, tunes };
}

const savedIds = (list: PlaylistDoc): string[] =>
  list.entries.flatMap((e) => (e.ref.kind === 'saved' ? [e.ref.id] : []));

/** Your saved rows that cannot travel: their tune is no longer on this device. */
export function missingSavedRows(list: PlaylistDoc, savedCharts: readonly Score[]): number {
  return savedIds(list).filter((id) => !savedCharts.some((s) => s.id === id)).length;
}

/** How many of your own tunes a set would carry. */
export function ownTunesIn(list: PlaylistDoc, savedCharts: readonly Score[]): number {
  return new Set(savedIds(list).filter((id) => savedCharts.some((s) => s.id === id))).size;
}

/** Write these tunes to your account marked shared (uploading any not there yet). */
export async function shareTunes(tunes: readonly Score[]): Promise<void> {
  const c = client();
  if (!c) throw new Error('Sign in to share tunes.');
  const now = new Date().toISOString();
  for (let i = 0; i < tunes.length; i += CHUNK) {
    const rows = tunes.slice(i, i + CHUNK).map((tune) => ({
      tune_id: tune.id,
      title: tune.title ?? '',
      composer: tune.composer ?? '',
      doc: tune as unknown as Row,
      visibility: 'shared',
      updated_at: now,
    }));
    const { error } = await c.from(TABLE).upsert(rows, { onConflict: 'owner,tune_id' });
    if (error) throw new Error(error.message);
  }
}

/**
 * Share a playlist with your tunes in it: share the tunes, then the playlist
 * that points at them. The tunes go first, so a playlist is never readable
 * before the tunes it names. Returns the version it shared, which becomes this
 * device's copy.
 */
export async function publishWithTunes(
  list: PlaylistDoc,
  savedCharts: readonly Score[],
): Promise<Awaited<ReturnType<typeof publishSetlist>> & { doc: PlaylistDoc }> {
  const owner = getAuthState().userId;
  if (!owner) throw new Error('Sign in to share playlists.');
  const { doc, tunes } = portableSetlist(list, savedCharts, owner);
  if (tunes.length) await shareTunes(tunes);
  return { ...(await publishSetlist(doc)), doc };
}

/** Open a tune someone shared, guarded like any tune another device wrote. */
export async function loadAccountTune(ref: { owner: string; id: string }): Promise<Score> {
  const c = client();
  if (!c) throw new Error('Sign in to open shared tunes.');
  const { data, error } = await c
    .from(TABLE)
    .select('tune_id, doc')
    .eq('owner', ref.owner)
    .eq('tune_id', ref.id);
  if (error) throw new Error(error.message);
  const row = data?.[0];
  const [score] = checkSavedTunes(
    row?.doc && typeof row.doc === 'object' ? [{ ...(row.doc as Score), id: ref.id }] : [],
  ).kept;
  if (!score) throw new Error('That tune is no longer shared.');
  return score;
}

export function __setSharedTunesTestClient(c: SharedTunesClient | null): void {
  testClient = c;
}
